import { type FragmentOf, graphql, readFragment } from "@opensea/graphql"
import { isAfter, isBefore } from "date-fns"
import { DropStatus } from "@/components/collection/drops/constants"

export const getDropStatusFragment = graphql(`
  fragment getDropStatus on Collection {
    drop {
      __typename
      ... on Erc721SeaDropV1 {
        maxSupply
        totalSupply
      }
      ... on Erc1155SeaDropV2 {
        tokenSupply {
          totalSupply
          maxSupply
        }
      }
      stages {
        startTime
        endTime
      }
    }
  }
`)

export const findActiveDropStageIndexFragment = graphql(
  `
    fragment findActiveDropStageIndex on Collection {
      ...getDropStatus
      drop {
        stages {
          stageIndex
          startTime
          endTime
        }
      }
    }
  `,
  [getDropStatusFragment],
)

export function findActiveDropStageIndex(
  collectionKey: FragmentOf<typeof findActiveDropStageIndexFragment>,
): {
  activeStageIndex: number
} {
  const collection = readFragment(
    findActiveDropStageIndexFragment,
    collectionKey,
  )

  if (!collection.drop?.stages) {
    return { activeStageIndex: -1 }
  }

  const status = getDropStatus(collection)
  const { stages } = collection.drop
  const now = Date.now()
  const firstStage = stages[0]
  let activeStageIndex = 0 // Public stage by default

  // stageIndex is 0 for public stage, and then chronological for each allowlist stage.
  // ex: in chronological order - 1, 2, 3, 0 for 3 allowlist stages and 1 public
  // we do not use the activeStage resolver since that's not live updated
  if (stages.length > 1) {
    if (status === DropStatus.MINTING_SOON) {
      activeStageIndex = firstStage.stageIndex
    } else if (status !== DropStatus.MINT_ENDED) {
      let currentStageIndex = null
      for (const stage of stages) {
        if (isAfter(now, stage.startTime) && isBefore(now, stage.endTime)) {
          currentStageIndex = stage.stageIndex
          break
        }
      }

      if (currentStageIndex !== null) {
        activeStageIndex = currentStageIndex
      } else {
        // If we went through all stages and didnt find a match, assign to first stage
        activeStageIndex = firstStage.stageIndex
      }
    }
  }

  return { activeStageIndex }
}

export function getDropStatus(
  collectionKey: FragmentOf<typeof getDropStatusFragment>,
): DropStatus | null {
  const collection = readFragment(getDropStatusFragment, collectionKey)
  const drop = collection.drop
  const now = Date.now()

  if (!drop?.stages?.length) {
    return null
  }

  const { totalSupply, maxSupply } =
    drop.__typename === "Erc1155SeaDropV2"
      ? drop.tokenSupply.reduce(
          (acc, token) => ({
            totalSupply: acc.totalSupply + Number(token.totalSupply),
            maxSupply: acc.maxSupply + Number(token.maxSupply),
          }),
          { totalSupply: 0, maxSupply: 0 },
        )
      : {
          totalSupply: Number(drop.totalSupply),
          maxSupply: Number(drop.maxSupply),
        }

  const firstStage = drop.stages[0]
  const finalStage = drop.stages.at(-1)

  let status = DropStatus.MINTING_SOON
  if (totalSupply >= maxSupply) {
    status = DropStatus.MINTED_OUT
  } else if (finalStage && isAfter(now, finalStage.endTime)) {
    status = DropStatus.MINT_ENDED
  } else if (
    isAfter(now, firstStage.startTime) &&
    finalStage &&
    isBefore(now, finalStage.endTime)
  ) {
    status = DropStatus.MINTING
  }

  return status
}
